import { type SubmitHandler, useForm } from 'react-hook-form';

import {
    CUSTOMIZATION_SUGGESTED_QUESTION_MAX_LENGTH,
    CUSTOMIZATION_SUGGESTED_QUESTION_MIN_LENGTH,
    CUSTOMIZATION_SUGGESTED_QUESTIONS_MAX_ITEMS,
    CustomizationAIMode,
} from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    Description,
    FieldError,
    FieldGroup,
    Form,
    Heading,
    Label,
    Panel,
    PanelBody,
    TextField,
    TextInput,
} from '@gitbook/spine-react';

import { useSiteSettingsContext } from '../SiteSettingsContext';

type SiteAICustomizationFormInputs = {
    suggestions: string[];
};

export function SiteAICustomizationPanel() {
    const { siteSettings, updateSiteSettings, savingSiteSettings } = useSiteSettingsContext();

    const form = useForm<SiteAICustomizationFormInputs>({
        defaultValues: {
            suggestions: siteSettings.ai.suggestions,
        },
        mode: 'onChange',
        disabled: savingSiteSettings,
    });

    const suggestions = form.watch('suggestions');

    const onSubmit: SubmitHandler<SiteAICustomizationFormInputs> = async (data) => {
        await updateSiteSettings(
            {
                ai: {
                    ...siteSettings.ai,
                    suggestions: data.suggestions.filter(
                        (suggestion) => suggestion.trim().length > 0
                    ),
                },
            },
            { title: 'Suggested questions saved' }
        );
        form.reset({ suggestions: data.suggestions });
    };

    const canUseCustomization = siteSettings.ai.mode !== CustomizationAIMode.None;

    return (
        <Panel>
            <Heading level={2}>Customize the experience</Heading>
            {!canUseCustomization ? (
                <Alert
                    kind={suggestions.length > 0 ? 'warning' : 'default'}
                    size="small"
                    title="These customizations only apply to AI experiences"
                    details={`You have turned off the AI experience. ${
                        suggestions.length > 0
                            ? 'Your configured customizations will not be applied. You can switch to one of the AI experiences above to use them.'
                            : 'These customizations only apply if you enable an AI experience above.'
                    }`}
                />
            ) : null}
            <PanelBody>
                <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
                    <FieldGroup orientation="vertical" className="gap-3">
                        <Label>Suggested questions</Label>
                        <Description>
                            Provide your own questions to get visitors started when they open{' '}
                            {siteSettings.ai.mode === CustomizationAIMode.Assistant
                                ? 'the Assistant'
                                : 'the search box'}
                            .
                        </Description>
                        {suggestions.map((suggestion, index) => (
                            <div key={index} className="flex gap-2">
                                <div className="flex grow flex-col gap-1">
                                    <TextField
                                        name={`suggestions.${index}`}
                                        minLength={CUSTOMIZATION_SUGGESTED_QUESTION_MIN_LENGTH}
                                        maxLength={CUSTOMIZATION_SUGGESTED_QUESTION_MAX_LENGTH}
                                    >
                                        <TextInput
                                            value={suggestion}
                                            placeholder={
                                                index === 0
                                                    ? 'How do I get started?'
                                                    : 'Add another question'
                                            }
                                            onChange={(event) => {
                                                const copy = [...suggestions];
                                                copy[index] = event.target.value;
                                                form.setValue('suggestions', copy, {
                                                    shouldDirty: true,
                                                });
                                            }}
                                            disabled={savingSiteSettings}
                                            aria-invalid={
                                                form.formState.errors.suggestions?.[index]
                                                    ? true
                                                    : undefined
                                            }
                                            maxLength={CUSTOMIZATION_SUGGESTED_QUESTION_MAX_LENGTH}
                                        />
                                        <FieldError />
                                    </TextField>
                                </div>
                                <Button
                                    iconOnly
                                    kind="blank"
                                    leadingIcon={Icon.Trashcan}
                                    label="Remove question"
                                    onPress={() => {
                                        form.setValue(
                                            'suggestions',
                                            suggestions.filter(
                                                (_, itemIndex) => itemIndex !== index
                                            ),
                                            { shouldDirty: true }
                                        );
                                    }}
                                    disabled={savingSiteSettings}
                                />
                            </div>
                        ))}
                        <div>
                            <Button
                                kind="secondary"
                                leadingIcon={Icon.Plus}
                                label="Add question"
                                onPress={() => {
                                    form.setValue('suggestions', [...suggestions, ''], {
                                        shouldDirty: true,
                                    });
                                }}
                                disabled={
                                    savingSiteSettings ||
                                    suggestions.length >=
                                        CUSTOMIZATION_SUGGESTED_QUESTIONS_MAX_ITEMS
                                }
                            />
                        </div>
                        {form.formState.isDirty ? (
                            <div className="mt-3 flex justify-end gap-2 border-t pt-3">
                                <Button
                                    label="Cancel"
                                    kind="blank"
                                    onPress={() => {
                                        form.reset({ suggestions: siteSettings.ai.suggestions });
                                    }}
                                    disabled={
                                        savingSiteSettings ||
                                        suggestions.length >
                                            CUSTOMIZATION_SUGGESTED_QUESTIONS_MAX_ITEMS
                                    }
                                />
                                <Button
                                    label="Save"
                                    type="submit"
                                    disabled={
                                        savingSiteSettings ||
                                        suggestions.length >
                                            CUSTOMIZATION_SUGGESTED_QUESTIONS_MAX_ITEMS
                                    }
                                />
                            </div>
                        ) : null}
                    </FieldGroup>
                </Form>
            </PanelBody>
        </Panel>
    );
}
